import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchHealthCheckProgress,
  fetchHealthChecks,
  HealthCheckDefinition,
  HealthCheckGroup,
  HealthCheckProgressResponse,
} from 'services/health';
import { HealthCheckState } from 'types/health';
import { buildMatrix } from '../components/HealthMatrixTable';

const isValidProgress = (progress: HealthCheckProgressResponse | undefined): progress is HealthCheckProgressResponse =>
  !!progress && typeof progress.completionPercent === 'number' && Array.isArray(progress.metrics);

type CheckStateMap = Record<string, HealthCheckState>;

export type UseHealthChecksResult = {
  groups: HealthCheckGroup[];
  loading: boolean;
  error: string | null;
  summaryText: string;
  totalChecks: number;
  hasStarted: boolean;
  allCompleted: boolean;
  checksNeedingRetryCount: number;
  checkStates: CheckStateMap;
  startChecks: () => void;
  retryAllChecks: () => void;
  retryIncompleteChecks: () => void;
  retryCheck: (checkId: string) => void;
  reloadChecks: () => Promise<void>;
  overallStatus: OverallStatus;
  isRunning: boolean;
};

export type OverallStatus = 'loading' | 'idle' | 'running' | 'attention' | 'error' | 'complete' | 'empty';

const useHealthChecks = (courseId: number): UseHealthChecksResult => {
  const [groups, setGroups] = useState<HealthCheckGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkStates, setCheckStates] = useState<CheckStateMap>({});
  const [hasStarted, setHasStarted] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const orderedChecks = useMemo<HealthCheckDefinition[]>(() => {
    const order: HealthCheckDefinition[] = [];

    groups.forEach((group) => {
      if ((group.layout ?? 'matrix') === 'matrix') {
        const rowLabel = group.config?.rowLabel ?? group.title;
        const { rows, columns } = buildMatrix(group.checks, rowLabel);
        rows.forEach((row) => {
          columns.forEach((column) => {
            const check = row.cells.get(column.ref);
            if (check) {
              order.push(check);
            }
          });
        });
        return;
      }

      order.push(...group.checks);
    });

    return order;
  }, [groups]);

  const checksNeedingRetry = useMemo<HealthCheckDefinition[]>(() => {
    return orderedChecks.filter((check) => {
      const state = checkStates[check.id];
      if (!state) {
        return false;
      }
      if (state.status === 'error' || state.status === 'skipped') {
        return true;
      }
      if (state.status === 'success') {
        const completion = state.progress?.completionPercent;
        return completion !== undefined && completion < 100;
      }
      return false;
    });
  }, [orderedChecks, checkStates]);

  const loadChecks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchHealthChecks(courseId);
      if (!response || !Array.isArray(response.checks)) {
        throw new Error('Unable to load health checks for this course.');
      }

      setGroups(response.checks);
      setCheckStates({});
      setHasStarted(false);
    } catch (err) {
      setGroups([]);
      setCheckStates({});
      setHasStarted(false);
      setError(err instanceof Error ? err.message : 'Unable to load health checks for this course.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void loadChecks();
  }, [loadChecks]);

  useEffect(() => {
    if (loading || error) {
      return;
    }

    setCheckStates((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.keys(next).forEach((checkId) => {
        if (!orderedChecks.find((check) => check.id === checkId)) {
          delete next[checkId];
          changed = true;
        }
      });

      orderedChecks.forEach((check) => {
        if (!next[check.id]) {
          next[check.id] = { status: 'pending' };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [orderedChecks, loading, error]);

  useEffect(() => {
    if (loading || error || !hasStarted || isFetching) {
      return;
    }

    const nextCheck = orderedChecks.find((check) => (checkStates[check.id]?.status ?? 'pending') === 'pending');
    if (!nextCheck) {
      return;
    }

    setIsFetching(true);
    setCheckStates((prev) => ({
      ...prev,
      [nextCheck.id]: { ...prev[nextCheck.id], status: 'loading', error: undefined },
    }));

    fetchHealthCheckProgress(nextCheck.endpoint)
      .then((progress) => {
        if (!isValidProgress(progress)) {
          throw new Error('Invalid response');
        }
        setCheckStates((prev) => ({
          ...prev,
          [nextCheck.id]: {
            status: 'success',
            progress,
          },
        }));
      })
      .catch(() => {
        setCheckStates((prev) => {
          const previous = prev[nextCheck.id];
          const status: 'error' | 'skipped' = previous?.status === 'success' ? 'skipped' : 'error';

          return {
            ...prev,
            [nextCheck.id]: {
              status,
              error: status === 'error' ? 'Unable to load this check. Please try again.' : undefined,
              progress: previous?.progress,
            },
          };
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [checkStates, orderedChecks, loading, error, hasStarted, isFetching]);

  const startChecks = useCallback(() => {
    setCheckStates(() => {
      const initial: CheckStateMap = {};
      orderedChecks.forEach((check) => {
        initial[check.id] = { status: 'pending' };
      });
      return initial;
    });
    setHasStarted(true);
  }, [orderedChecks]);

  const retryCheck = useCallback((checkId: string) => {
    setCheckStates((prev) => {
      const current = prev[checkId];
      if (current?.status === 'loading') {
        return prev;
      }

      return {
        ...prev,
        [checkId]: { status: 'pending' },
      };
    });
    setHasStarted(true);
  }, []);

  const retryAllChecks = useCallback(() => {
    setCheckStates(() => {
      const next: CheckStateMap = {};
      orderedChecks.forEach((check) => {
        next[check.id] = { status: 'pending' };
      });
      return next;
    });
    setHasStarted(true);
  }, [orderedChecks]);

  const retryIncompleteChecks = useCallback(() => {
    if (checksNeedingRetry.length === 0) {
      return;
    }

    setCheckStates((prev) => {
      const next = { ...prev };
      checksNeedingRetry.forEach((check) => {
        next[check.id] = { status: 'pending' };
      });
      return next;
    });

    setHasStarted(true);
  }, [checksNeedingRetry]);

  const progressSnapshot = useMemo(() => {
    const total = orderedChecks.length;
    let completed = 0;
    let running: HealthCheckDefinition | undefined;
    let skipped = 0;
    let hasPending = false;
    let errorCount = 0;

    orderedChecks.forEach((check) => {
      const status = checkStates[check.id]?.status;
      if (status === 'success') {
        completed += 1;
        return;
      }

      if (status === 'loading') {
        running = running ?? check;
        hasPending = true;
        return;
      }

      if (status === 'skipped') {
        skipped += 1;
        return;
      }

      if (status === 'error') {
        errorCount += 1;
        return;
      }

      if (status === undefined || status === 'pending') {
        hasPending = true;
      }
    });

    const remaining = total - completed;
    const allCompleted = total > 0 && !hasPending && errorCount === 0;

    let summary = 'No health checks available for this course.';
    if (total > 0) {
      summary = `Completed ${completed}/${total} checks`;
      if (running) {
        summary += ` • Running: ${running.label}`;
      } else if (remaining > 0) {
        summary += ' • Idle';
      }

      if (skipped > 0) {
        summary += ` • Skipped: ${skipped}`;
      }

      if (errorCount > 0) {
        summary += ` • Errors: ${errorCount}`;
      }
    }

    return {
      totalChecks: total,
      allCompleted,
      summaryText: summary,
      hasPending,
      errorCount,
    };
  }, [orderedChecks, checkStates]);

  const isRunning = hasStarted && (isFetching || progressSnapshot.hasPending);

  const overallStatus: OverallStatus = (() => {
    if (loading) {
      return 'loading';
    }
    if (error) {
      return 'error';
    }
    if (progressSnapshot.totalChecks === 0) {
      return 'empty';
    }
    if (!hasStarted) {
      return 'idle';
    }
    if (isRunning) {
      return 'running';
    }
    if (progressSnapshot.errorCount > 0) {
      return 'error';
    }
    if (checksNeedingRetry.length > 0) {
      return 'attention';
    }
    return 'complete';
  })();

  return {
    groups,
    loading,
    error,
    summaryText: progressSnapshot.summaryText,
    totalChecks: progressSnapshot.totalChecks,
    hasStarted,
    allCompleted: progressSnapshot.allCompleted,
    checksNeedingRetryCount: checksNeedingRetry.length,
    checkStates,
    startChecks,
    retryAllChecks,
    retryIncompleteChecks,
    retryCheck,
    reloadChecks: loadChecks,
    overallStatus,
    isRunning,
  };
};

export default useHealthChecks;
